import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { GeoJSON } from 'geojson';
import { DataDrivenPropertyValueSpecification } from 'maplibre-gl';
import { ColorEvent } from 'ngx-color';
import { firstValueFrom, forkJoin } from 'rxjs';

import { LayerBloc } from '@connect-services/client/blocs/layer.bloc';
import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import {
    GeometryFormGroup,
    LabelFormType,
    LayerSymbologyFormType,
} from '@connect-services/client/components/project-settings/symbology-edit/symbology-edit.component';
import { AssertService } from '@connect-services/client/services/assert.service';
import { ColorConverterService } from '@connect-services/client/services/color-converter.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { LayerEditService } from '@connect-services/client/services/layer-edit.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import {
    FeatureCollection,
    LayerAttribute,
    LayerStyle,
    LayerStylesService,
    MapboxStyle,
} from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export const DEFAULT_COLOR = '#000000';
export const NO_SYMBOL = 'noSymbol';
export const DEFAULT_SYMBOL = 'diamond';
export const DEFAULT_SYMBOL_SIZE = 0.625;
export const DEFAULT_LINE_SIZE = 3;
export const DEFAULT_FILL_OPACITY = 0.3;
export const DEFAULT_LABEL_SIZE = 10;
const DEFAULT_FILL_ZOOMS = { max: 12, min: 1 };
const DEFAUL_SYMBOL_AND_LINE_ZOOMS = { max: 24, min: 10 };

interface FormStyle {
    color: string | null;
    label: {
        label: string | null;
        maxZoom: number | null;
        minZoom: number | null;
        size: number | null;
    };
    maxZoom: number | null;
    minZoom: number | null;
    opacity: number | null;
    size?: number | null;
    symbol: string;
    symbolOpacity?: number | null;
}

type GeometryType = 'line' | 'point' | 'polygon';
type FormData = {
    [key in GeometryType]: FormStyle;
};

type LayerStyleProps = {
    [p: string]: string | number | (string | number | string[])[];
};

export enum SymbologyStyleScope {
    COMMON = 'common',
    SPECIFIC = 'specific',
}

@Injectable({
    providedIn: 'root',
})
export class SymbologyEditService {
    public readonly bookmarkedAttributes: WritableSignal<LayerAttribute[]> = signal([]);
    public readonly entitiesGeoJSON = signal<FeatureCollection | undefined>(undefined);
    public readonly formGroup: FormGroup<LayerSymbologyFormType> = new FormGroup({
        line: this.cleanLineFormGroup,
        point: this.cleanPointFormGroup,
        polygon: this.cleanPolygonFormGroup,
    });
    public readonly hasLine = computed(() => {
        return !!this.layerStyle()?.style.some((style: MapboxStyle) => style.type === MapboxStyle.TypeEnum.LINE);
    });
    public readonly hasPolygon = computed(() => {
        return !!this.layerStyle()?.style.some((style: MapboxStyle) => style.type === MapboxStyle.TypeEnum.FILL);
    });
    public readonly hasSymbol = computed(() => {
        return !!this.layerStyle()?.style.some(
            (style: MapboxStyle) => style.type === MapboxStyle.TypeEnum.SYMBOL && style.id !== 'label',
        );
    });
    public readonly isFormEmpty = computed(() => {
        return !this.isPointFormVisible() && !this.isLineFormVisible() && !this.isPolygonFormVisible();
    });
    public readonly isLineFormVisible = signal(false);
    public readonly isPointFormVisible = signal(false);
    public readonly isPolygonFormVisible = signal(false);
    public readonly isPreviewActive = signal(false);
    public readonly layerStyle = signal<LayerStyle | null>(null);
    public readonly layerStyleConnectField = signal<LayerStyle | null>(null);
    public readonly lineColor = signal(DEFAULT_COLOR);
    public readonly pointColor = signal(DEFAULT_COLOR);
    public readonly polygonColor = signal(DEFAULT_COLOR);
    public readonly sourcesVisibility: WritableSignal<{ [sourceId: string]: boolean }> = signal({});

    private readonly colorConverterService = inject(ColorConverterService);
    private readonly homeService = inject(HomeService);
    private readonly layerBloc = inject(LayerBloc);
    private readonly layerEditService = inject(LayerEditService);
    private readonly layerService = inject(LayerService);
    private readonly layerStyleService = inject(LayerStylesService);
    private readonly mapLayerService = inject(MapLayerService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly snackbarService = inject(SnackBarService);
    private readonly sourcesService = inject(SourcesService);

    public get cleanLineFormGroup(): FormGroup<GeometryFormGroup> {
        return this.buildGeometryFormGroup(MapboxStyle.TypeEnum.LINE);
    }

    public get cleanPointFormGroup(): FormGroup<GeometryFormGroup> {
        return this.buildGeometryFormGroup(MapboxStyle.TypeEnum.SYMBOL);
    }

    public get cleanPolygonFormGroup(): FormGroup<GeometryFormGroup> {
        return this.buildGeometryFormGroup(MapboxStyle.TypeEnum.FILL);
    }

    public get lineFormGroup(): FormGroup<GeometryFormGroup> {
        return this.formGroup.controls.line;
    }

    public get pointFormGroup(): FormGroup<GeometryFormGroup> {
        return this.formGroup.controls.point;
    }

    public get polygonFormGroup(): FormGroup<GeometryFormGroup> {
        return this.formGroup.controls.polygon;
    }

    public addLineForm(): void {
        this.isLineFormVisible.set(true);
        this.formGroup.addControl('line', this.cleanLineFormGroup);
        this.formGroup.markAsDirty();
    }

    public addPointForm(): void {
        this.isPointFormVisible.set(true);
        this.formGroup.addControl('point', this.cleanPointFormGroup);
        this.formGroup.markAsDirty();
    }

    public addPolygonForm(): void {
        this.isPolygonFormVisible.set(true);
        this.formGroup.addControl('polygon', this.cleanPolygonFormGroup);
        this.formGroup.markAsDirty();
    }

    public async loadStyle(): Promise<void> {
        const layer = this.layerBloc.value();

        const [layerStyle, layerStyleConnectField] = await Promise.all([
            this.loadLayerStyleById(layer?.layerStyle ?? null),
            this.loadLayerStyleById(layer?.layerStyleConnectField ?? null),
        ]);

        this.layerStyle.set(layerStyle);
        this.layerStyleConnectField.set(layerStyleConnectField);
    }

    public onChangeLabel(event: MatSelectChange, geometry: MapboxStyle.TypeEnum): void {
        const label: string = event.value;

        let group: FormGroup<GeometryFormGroup>;
        switch (geometry) {
            case MapboxStyle.TypeEnum.LINE:
                group = this.lineFormGroup;
                break;
            case MapboxStyle.TypeEnum.FILL:
                group = this.polygonFormGroup;
                break;
            default:
                group = this.pointFormGroup;
        }

        const zoomDefaults = geometry === MapboxStyle.TypeEnum.FILL ? DEFAULT_FILL_ZOOMS : DEFAUL_SYMBOL_AND_LINE_ZOOMS;

        const form = group.getRawValue();
        const size = form?.label?.size ?? DEFAULT_LABEL_SIZE;
        const minZoom = form?.label?.minZoom ?? zoomDefaults.min;
        const maxZoom = form?.label?.maxZoom ?? zoomDefaults.max;

        const labelParams = label
            ? { label, maxZoom, minZoom, size }
            : { label, maxZoom: null, minZoom: null, size: null };

        group.controls.label.patchValue(labelParams);
    }

    public onChangeLineColor(event: ColorEvent): void {
        this.lineColor.set(event.color.hex);
        this.formGroup.controls.line!.controls.color.setValue(event.color.hex, { emitEvent: true });
        this.formGroup.markAsDirty();
    }

    public onChangePointColor(event: ColorEvent): void {
        this.pointColor.set(event.color.hex);
        this.formGroup.controls.point!.controls.color.setValue(event.color.hex, { emitEvent: true });
        this.formGroup.markAsDirty();
    }

    public onChangePolygonColor(event: ColorEvent): void {
        this.polygonColor.set(event.color.hex);
        this.formGroup.controls.polygon!.controls.color.setValue(event.color.hex, { emitEvent: true });
        this.formGroup.markAsDirty();
    }

    public onChangePolygonSymbol(event: MatSelectChange): void {
        const form = this.polygonFormGroup.getRawValue();
        const symbolOpacity = event.value === NO_SYMBOL ? null : (form.symbolOpacity ?? DEFAULT_FILL_OPACITY);

        this.polygonFormGroup.controls.symbolOpacity?.setValue(symbolOpacity);
    }

    public previewStyle(reset: boolean = false): void {
        if (reset) {
            if (this.layerStyle()?.style) {
                this.findLayerSourceAndPreview(this.layerStyle()!.style, false);
            }

            this.isPreviewActive.set(false);

            return;
        }

        const layerStyle = this.layerStyle();
        if (this.formGroup.invalid || !layerStyle) {
            return;
        }

        // on preview, you need to force the visibility to actually see the updated style, even if the layer is hidden
        const mergedStyle = this.buildConnectServicesStyles(layerStyle)?.map((style: MapboxStyle) => ({
            ...style,
            visibility: 'visible',
        }));

        if (!mergedStyle) {
            return;
        }

        this.findLayerSourceAndPreview(mergedStyle);
        this.isPreviewActive.set(true);
    }

    public removeLineForm(): void {
        if (!this.formGroup.get('line')) {
            return;
        }

        this.isLineFormVisible.set(false);
        this.lineFormGroup.reset();
        this.lineFormGroup.controls.symbol.setValue(NO_SYMBOL, { emitEvent: true });
        this.lineFormGroup.controls.color.setValue(DEFAULT_COLOR, { emitEvent: true });
        this.lineFormGroup.patchValue(
            {
                color: DEFAULT_COLOR,
                size: DEFAULT_LINE_SIZE,
                symbol: NO_SYMBOL,
            },
            { emitEvent: true },
        );
        this.lineColor.set(DEFAULT_COLOR);
        this.formGroup.markAsDirty();
    }

    public removePointForm(): void {
        if (!this.formGroup.get('point')) {
            return;
        }

        this.isPointFormVisible.set(false);
        this.pointFormGroup.reset();
        this.pointFormGroup.patchValue(
            {
                color: DEFAULT_COLOR,
                size: DEFAULT_SYMBOL_SIZE,
                symbol: DEFAULT_SYMBOL,
            },
            { emitEvent: true },
        );
        this.pointColor.set(DEFAULT_COLOR);
        this.formGroup.markAsDirty();
    }

    public removePolygonForm(): void {
        if (!this.formGroup.get('polygon')) {
            return;
        }

        this.isPolygonFormVisible.set(false);
        this.polygonFormGroup.reset();
        this.polygonFormGroup.patchValue(
            {
                color: DEFAULT_COLOR,
                opacity: DEFAULT_FILL_OPACITY,
                symbol: NO_SYMBOL,
            },
            { emitEvent: true },
        );
        this.polygonColor.set(DEFAULT_COLOR);
        this.formGroup.markAsDirty();
    }

    public resetForm(): void {
        this.formGroup.reset({
            line: this.cleanLineFormGroup.value,
            point: this.cleanPointFormGroup.value,
            polygon: this.cleanPolygonFormGroup.value,
        });
        this.pointColor.set(DEFAULT_COLOR);
        this.lineColor.set(DEFAULT_COLOR);
        this.polygonColor.set(DEFAULT_COLOR);
        this.isPointFormVisible.set(false);
        this.isLineFormVisible.set(false);
        this.isPolygonFormVisible.set(false);

        this.previewStyle(true);
        this.sourcesVisibility.set({});
    }

    public async setBookmarkedAttributes(): Promise<void> {
        const attributes = await this.layerService.getAttributes(this.layerBloc.value()!);
        const bookmarkedAttributes: LayerAttribute[] = attributes.filter(
            (attribute: LayerAttribute) => attribute.isBookmarked,
        );
        this.bookmarkedAttributes.set([
            {
                column: '',
                isBookmarked: true,
                order: null,
            },
            {
                column: 'businessIdentifier',
                isBookmarked: true,
                order: null,
            },
            ...bookmarkedAttributes,
        ]);
    }

    public setSourcesVisibility(): void {
        const sourcesVisibility = this.sourcesService
            .sources()
            .reduce((acc: Record<string, boolean>, source: MapBaseSource<unknown>): Record<string, boolean> => {
                return { ...acc, [source.sourceId]: this.mapSourceService.isSourceVisible(source) };
            }, {});

        this.sourcesVisibility.set(sourcesVisibility);
    }

    public async submit(): Promise<void> {
        if (this.formGroup.invalid) {
            return;
        }

        const layerStyle = this.layerStyle();
        const layerStyleConnectField = this.layerStyleConnectField();
        if (!layerStyle || !layerStyleConnectField) {
            return;
        }

        const connectServicesStyles = this.buildConnectServicesStyles(layerStyle);
        const connectFieldStyles = this.buildConnectFieldStyles(layerStyleConnectField);

        if (!connectServicesStyles.length || !connectFieldStyles.length) {
            return;
        }

        await firstValueFrom(
            forkJoin([
                this.layerStyleService.layerStyleControllerPatch({
                    id: layerStyle.id,
                    updateLayerStyleDto: {
                        style: connectServicesStyles,
                    },
                }),
                this.layerStyleService.layerStyleControllerPatch({
                    id: layerStyleConnectField.id,
                    updateLayerStyleDto: {
                        style: connectFieldStyles,
                    },
                }),
            ]),
        );

        this.homeService.resetSources(true);
        await this.homeService.initSources();

        // need to wait for the sources to be initialized
        setTimeout(() => {
            this.updateSourcesVisibility();
        }, 300);

        this.layerEditService.isEditingSymbology.set(false);
    }

    private buildConnectFieldStyles(baseStyle: LayerStyle): MapboxStyle[] {
        return this.buildStylesPayload(SymbologyStyleScope.COMMON, baseStyle.style);
    }

    private buildConnectServicesStyles(baseStyle: LayerStyle): MapboxStyle[] {
        return this.buildStylesPayload(SymbologyStyleScope.SPECIFIC, baseStyle.style);
    }

    private buildGeometryFormGroup(geometry: MapboxStyle.TypeEnum): FormGroup<GeometryFormGroup> {
        const opacity = geometry === MapboxStyle.TypeEnum.FILL ? DEFAULT_FILL_OPACITY : null;
        const symbol = geometry === MapboxStyle.TypeEnum.SYMBOL ? DEFAULT_SYMBOL : NO_SYMBOL;
        const controls: GeometryFormGroup = {
            color: new FormControl(DEFAULT_COLOR, { nonNullable: true }),
            label: this.buildLabelFormGroup(),
            maxZoom: new FormControl<number | null>(null),
            minZoom: new FormControl<number | null>(null),
            opacity: new FormControl<number | null>(opacity),
            symbol: new FormControl(symbol, { nonNullable: true }),
        };

        if (geometry === MapboxStyle.TypeEnum.FILL) {
            controls.symbolOpacity = new FormControl<number | null>(null);
        } else {
            controls.size = new FormControl<number | null>(this.getDefaultSize(geometry));
            controls.opacity = new FormControl<number | null>(DEFAULT_FILL_OPACITY);
        }

        return new FormGroup<GeometryFormGroup>(controls, {
            validators: [ValidatorsService.zoomRangeValidator],
        });
    }

    private buildLabelFormGroup(): FormGroup<LabelFormType> {
        return new FormGroup<LabelFormType>(
            {
                label: new FormControl<string | null>(null),
                maxZoom: new FormControl<number | null>(null),
                minZoom: new FormControl<number | null>(null),
                size: new FormControl<number | null>(null),
            },
            {
                validators: [ValidatorsService.zoomRangeValidator],
            },
        );
    }

    private buildLabelStyle(
        key: MapboxStyle.TypeEnum,
        label: {
            label: string | null;
            maxZoom: number | null;
            minZoom: number | null;
            size: number | null;
        },
    ): MapboxStyle {
        return {
            id: `label-${key}`,
            layout: {
                'symbol-placement': key === MapboxStyle.TypeEnum.LINE ? 'line' : 'point',
                'text-anchor': 'top-left',
                'text-field': label.label ? ['get', label.label] : '',
                'text-font': ['DM Sans'],
                'text-offset': [0, -2],
                'text-size': label.size ?? 10,
            },
            maxzoom: label.maxZoom ?? undefined,
            minzoom: label.minZoom ?? undefined,
            paint: {},
            source: 'composite',
            type: 'symbol',
        };
    }

    private buildPaintLayoutData(
        geometryType: MapboxStyle.TypeEnum,
        form: FormStyle,
        target: SymbologyStyleScope = SymbologyStyleScope.SPECIFIC,
    ): MapboxStyle {
        const paint: LayerStyleProps = {};
        const layout: LayerStyleProps = {};
        const isCommonOnlyTarget = target === SymbologyStyleScope.COMMON;

        switch (geometryType) {
            case MapboxStyle.TypeEnum.LINE:
                if (!isCommonOnlyTarget && form.opacity !== null) {
                    paint['line-opacity'] = form.opacity;
                }
                if (form.color) {
                    paint['line-color'] = this.colorConverterService.hexToRgb(form.color) ?? 'rgb(0,0,0)';
                }
                paint['line-width'] = form.size ?? DEFAULT_LINE_SIZE;
                break;
            case MapboxStyle.TypeEnum.FILL:
                paint['fill-opacity'] = form.opacity ?? DEFAULT_FILL_OPACITY;
                paint['fill-color'] = this.colorConverterService.hexToRgb(form.color ?? '') ?? 'rgb(0,0,0)';
                break;
            case MapboxStyle.TypeEnum.SYMBOL:
            default:
                if (!isCommonOnlyTarget && form.opacity !== null) {
                    paint['icon-opacity'] = form.opacity;
                }

                if (form.color) {
                    paint['icon-color'] = this.colorConverterService.hexToRgb(form.color) ?? 'rgb(0,0,0)';
                }

                layout['icon-size'] = form.size ?? '';
                layout['icon-image'] = form.symbol;
                break;
        }

        const zoomProps = isCommonOnlyTarget
            ? {}
            : {
                  maxzoom: form.maxZoom ?? undefined,
                  minzoom: form.minZoom ?? undefined,
              };

        return {
            id: geometryType,
            layout,
            ...zoomProps,
            ...(form.symbol !== NO_SYMBOL && !isCommonOnlyTarget ? { metadata: { legendGroup: form.symbol } } : {}),

            paint,
            source: 'composite',
            type: geometryType,
        };
    }

    private buildStylesPayload(target: SymbologyStyleScope, baseStyles: MapboxStyle[]): MapboxStyle[] {
        const formData: FormData = this.formGroup.getRawValue();
        const formStyle = this.transformFormDataToLayerStyle(formData, target);

        if (!formStyle.length) {
            this.snackbarService.openWarningSnackBar('warningAtLeastOneStyle');

            return [];
        }

        return this.mergeLayerStyles(baseStyles, formStyle);
    }

    private findLayerSourceAndPreview(mergedStyle: MapboxStyle[], forceVisibility: boolean = true): void {
        const layerSource = this.sourcesService.sources().find((source: MapBaseSource<unknown>) => {
            AssertService.hasKey(source.data, 'id');

            return source.data['id'] === this.layerBloc.value()!.id;
        });
        if (!layerSource) {
            return;
        }

        if (
            layerSource.sourceId in this.sourcesVisibility() &&
            this.sourcesVisibility()![layerSource.sourceId] !== layerSource.isVisible
        ) {
            layerSource.isVisible = this.sourcesVisibility()![layerSource.sourceId];
        }

        this.mapLayerService.previewLayerStyle(layerSource, mergedStyle, forceVisibility);

        const mapSource = this.mapLayerService.getMapSource(layerSource.sourceId);
        if (!mapSource || !this.entitiesGeoJSON()) {
            return;
        }

        mapSource.setData(this.entitiesGeoJSON()! as GeoJSON);
    }

    private getDefaultSize(type: MapboxStyle.TypeEnum): number | null {
        switch (type) {
            case MapboxStyle.TypeEnum.SYMBOL:
                return DEFAULT_SYMBOL_SIZE;
            case MapboxStyle.TypeEnum.LINE:
                return DEFAULT_LINE_SIZE;
            default:
                return null;
        }
    }

    private getGeometryType(key: GeometryType): MapboxStyle.TypeEnum {
        switch (key) {
            case 'line':
                return MapboxStyle.TypeEnum.LINE;
            case 'polygon':
                return MapboxStyle.TypeEnum.FILL;
            default:
                return MapboxStyle.TypeEnum.SYMBOL;
        }
    }

    private getSymbolStyleForGeometry(geometry: MapboxStyle.TypeEnum, form: FormStyle): MapboxStyle {
        const symbolOpacity = form.symbolOpacity;

        let sizeOrSymbolOpacity = {};
        if (geometry === MapboxStyle.TypeEnum.LINE) {
            sizeOrSymbolOpacity = {
                ['line-width']: (form.size ?? 3) * 4, // this is the symbol size
            };
        } else if (geometry === MapboxStyle.TypeEnum.FILL) {
            sizeOrSymbolOpacity = {
                ['fill-opacity']: symbolOpacity ?? DEFAULT_FILL_OPACITY,
            };
        }

        return {
            id: `${geometry}-2`,
            layout: {},
            ...(form.symbol !== NO_SYMBOL ? { metadata: { legendGroup: form.symbol } } : {}),
            maxzoom: form.maxZoom ?? undefined,
            minzoom: form.minZoom ?? undefined,
            paint: {
                ...(form.symbol !== NO_SYMBOL ? { [`${geometry}-pattern`]: form.symbol } : {}),
                ...sizeOrSymbolOpacity,
            },
            source: 'composite',
            type: geometry,
        };
    }

    private inheritSpecificProps(baseStyles: MapboxStyle[], formStyle: MapboxStyle): MapboxStyle {
        const sameTypeBaseStyle = baseStyles.find(
            (baseStyle: MapboxStyle) =>
                baseStyle.type === formStyle.type &&
                (formStyle.type !== MapboxStyle.TypeEnum.SYMBOL || !formStyle.id.startsWith('label-')),
        );

        if (formStyle.id.startsWith('label-')) {
            const type = formStyle.id.split('-').at(-1);
            const sameTypeStyle = baseStyles.find((baseStyle: MapboxStyle) => baseStyle.type === type);

            AssertService.isEnum<MapboxStyle.TypeEnum>(type, MapboxStyle.TypeEnum);
            // The SDK defines the filter as object[], and in TS, `object` excludes primitives, so `string[]` is not assignable to `object[]`
            formStyle.filter =
                sameTypeStyle?.filter ||
                (this.mapLayerService.getDefaultFilter(type) as unknown as object[] | undefined);
        } else {
            formStyle.filter = this.mapLayerService.getDefaultFilter(formStyle.type) as unknown as object[] | undefined;
        }

        AssertService.isEnum<MapboxStyle.TypeEnum>(formStyle.type, MapboxStyle.TypeEnum);
        const cloned = { ...formStyle };

        switch (formStyle.type) {
            case MapboxStyle.TypeEnum.FILL:
                cloned.layout = {
                    'fill-sort-key':
                        sameTypeBaseStyle && 'fill-sort-key' in sameTypeBaseStyle.layout!
                            ? sameTypeBaseStyle!.layout['fill-sort-key']
                            : 1,
                    ...cloned.layout,
                };
                break;
            case MapboxStyle.TypeEnum.LINE:
                cloned.layout = {
                    'line-sort-key':
                        sameTypeBaseStyle && 'line-sort-key' in sameTypeBaseStyle.layout!
                            ? sameTypeBaseStyle!.layout['line-sort-key']
                            : 1,
                    ...cloned.layout,
                };
                break;
            case MapboxStyle.TypeEnum.SYMBOL:
                cloned.layout = {
                    'symbol-sort-key':
                        sameTypeBaseStyle && 'symbol-sort-key' in sameTypeBaseStyle.layout!
                            ? sameTypeBaseStyle!.layout['symbol-sort-key']
                            : 1,
                    ...cloned.layout,
                };

                if (!cloned.id.startsWith('label-')) {
                    cloned.layout = {
                        'icon-allow-overlap':
                            sameTypeBaseStyle && 'icon-allow-overlap' in sameTypeBaseStyle.layout!
                                ? sameTypeBaseStyle!.layout['icon-allow-overlap']
                                : true,
                        ...cloned.layout,
                    };
                    cloned.paint = {
                        'icon-halo-blur': 0.1,
                        'icon-halo-color': 'rgb(0, 0, 0)',
                        'icon-halo-width': 3,
                        ...cloned.paint,
                    };
                }
                break;
        }

        return cloned;
    }

    private loadLayerStyleById(id: string | null): Promise<LayerStyle | null> {
        if (!id) {
            return Promise.resolve(null);
        }

        return firstValueFrom(this.layerStyleService.layerStyleControllerGetOne({ id }));
    }

    private mergeLayerStyles(baseStyles: MapboxStyle[], formStyles: MapboxStyle[]): MapboxStyle[] {
        const merged: MapboxStyle[] = [];

        for (const formStyle of formStyles) {
            const baseStyle = baseStyles.find((style: MapboxStyle) => style.id === formStyle.id);

            if (!baseStyle) {
                const updatedFormStyle = this.inheritSpecificProps(baseStyles, formStyle);
                merged.push(updatedFormStyle);

                continue;
            }

            const resultLayer: MapboxStyle = {
                filter:
                    formStyle.filter ||
                    baseStyle.filter ||
                    (this.mapLayerService.getDefaultFilter(baseStyle.type) as unknown as object[] | undefined),
                id: formStyle.id,
                layout: this.mergeObject(baseStyle.layout, formStyle.layout, this.mergeLayoutObject(baseStyle)),
                maxzoom: formStyle.maxzoom,
                metadata:
                    formStyle.metadata && Object.keys(formStyle.metadata).length
                        ? this.mergeObject(baseStyle.metadata, formStyle.metadata)
                        : {},
                minzoom: formStyle.minzoom,
                paint: this.mergeObject(baseStyle.paint, formStyle.paint, this.mergePaintObject(baseStyle)),
                source: formStyle.source || baseStyle.source || 'composite',
                type: formStyle.type || baseStyle.type,
            };

            merged.push(resultLayer);
        }

        return merged;
    }

    private mergeLayoutObject(baseStyle: MapboxStyle): (key: string, value: unknown, baseValue?: unknown) => unknown {
        return (key: string, value: unknown): unknown => {
            if (!baseStyle?.layout || baseStyle.type !== MapboxStyle.TypeEnum.SYMBOL) {
                return value;
            }

            if (key !== 'text-size' || !Array.isArray(value)) {
                return value;
            }

            AssertService.hasKey(baseStyle.layout, 'text-size');
            AssertService.isSymbolLayerSpecification(baseStyle);

            const baseValue: DataDrivenPropertyValueSpecification<number> | undefined =
                baseStyle!.layout?.['text-size'];

            if (Array.isArray(value) && Array.isArray(baseValue) && baseValue[0] === 'step') {
                const updated = [...baseValue];
                updated[updated.length - 1] = value[value.length - 1];

                return updated;
            }

            return value;
        };
    }

    private mergeObject(
        base: object = {},
        update: object = {},
        customResolver?: (key: string, value: unknown, baseValue?: unknown) => unknown,
    ): object {
        return Object.entries(update)
            .filter(([, value]) => value !== '' || value !== null || value !== 0)
            .reduce(
                (result: Record<string, unknown>, [key, value]: [string, unknown]) => {
                    try {
                        AssertService.hasKey(base, key);
                        result[key] = customResolver ? customResolver(key, value, base[key]) : value;
                    } catch {
                        result[key] = value;
                    }

                    return result;
                },
                { ...base },
            );
    }

    private mergePaintObject(baseStyle: MapboxStyle): (key: string, value: unknown, baseValue?: unknown) => unknown {
        return (key: string, value: unknown): unknown => {
            if (!baseStyle?.paint || key !== 'line-width') {
                return value;
            }

            AssertService.hasKey(baseStyle.paint, key);

            const baseValue = baseStyle!.paint[key];

            if (Array.isArray(baseValue) && baseValue[0] === 'step' && !Array.isArray(value)) {
                const updated = [...baseValue];
                updated[updated.length - 1] = value;

                return updated;
            }

            return value;
        };
    }

    private setSourcesWithUpdatedVisibility(): void {
        const updatedVisibilitySources = this.sourcesService.sources().map((source: MapBaseSource<unknown>) => {
            return {
                ...source,
                ...(this.sourcesVisibility()![source.sourceId]
                    ? { isVisible: this.sourcesVisibility()![source.sourceId] }
                    : {}),
                style: source.style,
            };
        });
        this.sourcesService.sources.set(updatedVisibilitySources);
    }

    private toggleSourcesVisibility(): void {
        this.sourcesService.sources().forEach((source: MapBaseSource<unknown>) => {
            if (
                this.sourcesVisibility()![source.sourceId] === undefined ||
                this.sourcesVisibility()![source.sourceId] === source.isVisible
            ) {
                return;
            }

            source.isVisible = this.sourcesVisibility()![source.sourceId];
            this.mapSourceService.toggleSourceVisibility(source);
        });
    }

    private transformFormDataToLayerStyle(
        formData: FormData,
        target: SymbologyStyleScope = SymbologyStyleScope.SPECIFIC,
    ): MapboxStyle[] {
        const styleLayers: MapboxStyle[] = [];

        const geometries: { hasGeometryForm: boolean; key: GeometryType }[] = [
            { hasGeometryForm: this.isLineFormVisible(), key: 'line' },
            { hasGeometryForm: this.isPointFormVisible(), key: 'point' },
            { hasGeometryForm: this.isPolygonFormVisible(), key: 'polygon' },
        ];

        geometries
            .filter((geometry: { hasGeometryForm: boolean; key: GeometryType }) => geometry.hasGeometryForm)
            .map((geometry: { hasGeometryForm: boolean; key: GeometryType }) => {
                AssertService.hasKey(formData, geometry.key);
                const form: FormStyle = formData[geometry.key];
                if (!form) {
                    return;
                }

                const geometryType = this.getGeometryType(geometry.key);
                let symbolStyle: MapboxStyle | null = null;
                const isCommonOnlyTarget = target === SymbologyStyleScope.COMMON;

                if (!isCommonOnlyTarget && form.symbol !== NO_SYMBOL && geometryType !== MapboxStyle.TypeEnum.SYMBOL) {
                    symbolStyle = this.getSymbolStyleForGeometry(geometryType, form);
                    styleLayers.push(symbolStyle);
                }

                styleLayers.push(this.buildPaintLayoutData(geometryType, form, target));

                if (isCommonOnlyTarget) {
                    return;
                }

                const label = form.label;
                if (label?.label) {
                    styleLayers.push(this.buildLabelStyle(geometryType, label));
                }
            });

        return styleLayers;
    }

    private updateSourcesVisibility(): void {
        this.setSourcesWithUpdatedVisibility();

        if (!this.sourcesService.sources()?.length) {
            return;
        }

        this.toggleSourcesVisibility();
    }
}
